import { encerrarSessao } from "./sessao";
import { useEffect, useState } from "react";
import AnimatedBackground from "./components/AnimatedBackground";
import Login from "./components/Login";
import Workspace from "./components/Workspace";
import Usuarios from "./components/Usuarios";
import SolicitarAtualizacaoView from "./components/SolicitarAtualizacaoView";
import CybersegDashboard from "./components/CybersegDashboard";
import { setSessionUser } from "./api";
import { IconSun, IconMoon, IconShield } from "./icons";
import { useTheme } from "./theme";
import { isAdminCyberseg, isAdminSupremo } from "./access";
import type { User } from "./types";

/**
 * Administrador = Admin Supremo (login global, sempre) OU marcado como
 * Administrador no cadastro do Portal (o backend manda `admin` já resolvido no
 * login). O CARGO não entra mais na conta: era ele que fazia a tela continuar
 * mostrando o menu de administrador depois de DESMARCAR a opção — bastava o
 * cargo ter "admin" (até "Administrativo").
 *
 * A sessão fica salva no navegador e as antigas não têm o campo `admin`; por
 * isso o App revalida o perfil no servidor ao abrir (ver useEffect abaixo) —
 * sem isso, quem já estava logado perdia o menu "Usuários" até sair e entrar.
 */
const ehAdmin = (u: User | null) =>
  !!u && (isAdminSupremo(u.usuario) || u.admin === true);
import logoVerde from "./assets/logo-verde.png";
import logoBranca from "./assets/logo-branca.png";

const USER_KEY = "sercamp-portal-user";

function loadUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

export default function App() {
  const { theme, toggle } = useTheme();
  const [user, setUser] = useState<User | null>(loadUser);
  const [view, setView] = useState<"workspace" | "usuarios" | "solicitar">("workspace");
  const isAdmin = ehAdmin(user);
  const isCyberseg = isAdminCyberseg(user?.usuario);

  // Identidade enviada no header das operações de usuários (validação no servidor).
  useEffect(() => {
    setSessionUser(user?.usuario ?? "");
  }, [user]);

  // Revalida o perfil no servidor ao abrir o Portal (e a cada troca de login).
  // A sessão vive no localStorage e envelhece: uma sessão gravada antes do
  // checkbox "Administrador" não tem o campo `admin`, e uma pessoa que foi
  // desmarcada continuaria vendo o menu de admin até sair e entrar. Aqui o
  // servidor é a fonte: o que ele responde substitui o que estava guardado.
  // Falha de rede ou 404 não desloga ninguém — mantém o que já havia.
  const login = user?.usuario ?? "";
  useEffect(() => {
    if (!login) return;
    let cancelado = false;
    (async () => {
      try {
        const r = await fetch(`/api/perfil?u=${encodeURIComponent(login)}`);
        if (!r.ok) return;
        const p = (await r.json()) as { admin?: boolean; cargo?: string | null; nome?: string | null } | null;
        // Resposta resumida (sem sessão válida): não mexe no que está guardado.
        if (!p || cancelado || !("admin" in p)) return;
        setUser((atual) => {
          if (!atual || atual.usuario !== login) return atual;
          const admin = p.admin === true;
          if (atual.admin === admin && atual.cargo === (p.cargo ?? null)) return atual;
          const novo = { ...atual, admin, cargo: p.cargo ?? null };
          try {
            localStorage.setItem(USER_KEY, JSON.stringify(novo));
          } catch {
            /* navegador sem armazenamento: a sessão desta aba já está correta */
          }
          return novo;
        });
      } catch {
        /* Portal fora do ar ou sem rede: segue com a sessão guardada */
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [login]);

  // Não-admin nunca fica na tela de usuários.
  useEffect(() => {
    if (view === "usuarios" && !isAdmin) setView("workspace");
  }, [view, isAdmin]);

  function handleLogin(u: User) {
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    // Síncrono, ANTES do setUser: componentes que já nascem montados no próximo
    // render (ex.: painel do Admin Cybersecurity) não podem esperar o useEffect
    // abaixo, que roda depois do efeito deles.
    setSessionUser(u.usuario ?? "");
    setUser(u);
  }

  function handleLogout() {
    localStorage.removeItem(USER_KEY);
    encerrarSessao();
    setView("workspace");
    setUser(null);
  }

  const themeBtn = (
    <button
      className="theme-toggle"
      onClick={toggle}
      title="Alternar tema"
      aria-label="Alternar tema claro/escuro"
      type="button"
    >
      {theme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
    </button>
  );

  // ---- Tela de login (layout corporativo de duas colunas, padrão da suíte) ----
  if (!user) {
    return <Login onSuccess={handleLogin} theme={theme} onToggleTheme={toggle} />;
  }

  // ---- Admin Cybersecurity: SOMENTE o painel de auditoria/segurança, nada mais ----
  if (isCyberseg) {
    return (
      <>
        <AnimatedBackground />
        <div className="app">
          <header className="appbar">
            <img className="logo logo-dark" src={logoVerde} alt="SERCAMP" />
            <img className="logo logo-light" src={logoBranca} alt="SERCAMP" />
            <div className="appbar-right">
              <div className="appbar-user">
                <IconShield size={16} />
                <span className="appbar-user-name">{user.nome}</span>
                <span className="appbar-user-role">Auditoria &amp; Segurança</span>
              </div>
              {themeBtn}
              <button className="logout-btn" onClick={handleLogout} title="Sair">
                Sair
              </button>
            </div>
          </header>
          <main className="content">
            <CybersegDashboard />
          </main>
          <footer className="foot app-foot">© 2026 SERCAMP · Portal de Sistemas</footer>
        </div>
      </>
    );
  }

  // ---- Área de trabalho (dashboard de largura cheia) ----
  return (
    <>
      <AnimatedBackground />
      <div className="app">
        <header className="appbar">
          <img className="logo logo-dark" src={logoVerde} alt="SERCAMP" />
          <img className="logo logo-light" src={logoBranca} alt="SERCAMP" />
          <div className="appbar-right">
            <div className="appbar-user">
              <span className="appbar-user-name">{user.nome}</span>
              {user.cargo && (
                <span className="appbar-user-role">{user.cargo}</span>
              )}
            </div>
            {isAdmin && (
              <button
                className="logout-btn"
                onClick={() =>
                  setView((v) => (v === "usuarios" ? "workspace" : "usuarios"))
                }
                title="Gerenciar usuários"
              >
                {view === "usuarios" ? "Sistemas" : "Usuários"}
              </button>
            )}
            {/* O mesmo canal que existe dentro de cada sistema, aqui no Portal:
                quem não estiver com um sistema aberto ainda consegue pedir um
                ajuste sem procurar o desenvolvedor por WhatsApp. */}
            <button
              className="logout-btn"
              onClick={() => setView((v) => (v === "solicitar" ? "workspace" : "solicitar"))}
              title="Pedir um ajuste ao desenvolvimento"
            >
              {view === "solicitar" ? "Sistemas" : "Solicitar Atualização"}
            </button>
            {themeBtn}
            <button className="logout-btn" onClick={handleLogout} title="Sair">
              Sair
            </button>
          </div>
        </header>

        <main className="content">
          {view === "usuarios" ? (
            <Usuarios onBack={() => setView("workspace")} />
          ) : view === "solicitar" ? (
            <SolicitarAtualizacaoView usuario={user.usuario} />
          ) : (
            <Workspace user={user} />
          )}
        </main>

        <footer className="foot app-foot">
          © 2026 SERCAMP · Portal de Sistemas
        </footer>
      </div>
    </>
  );
}
